import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import AdminLayout from '../../components/AdminLayout';
import {
  FiUsers, FiPlus, FiSearch, FiRefreshCw, FiTrash2, FiEye, FiDownload,
  FiShield, FiStar, FiSettings, FiX, FiCopy, FiCheck
} from 'react-icons/fi';

// Import shared admin components
import {
  AdminContainer, AdminHeader, HeaderContent, HeaderText, HeaderActions,
  ActionButton, MainContent, StatsGrid, StatCard, StatHeader, StatIcon,
  StatValue, StatLabel, SearchAndFilters, SearchBox, FilterSelect,
  ActionButtonGroup, DataTable, TableActionButton, Modal, ModalContent,
  ModalActions, ResultsCounter, LoadingSpinner, ErrorMessage
} from '../../styles/AdminComponents';

// Modal form specific styled components
import styled from 'styled-components';

const ModalFormGroup = styled.div`
  margin-bottom: 1rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
  }
  
  input, select {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-size: 1rem;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
`;

const CredentialsDisplay = styled.div`
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1rem 0;
  
  .credential-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem;
    background: white;
    border-radius: 8px;
    margin-bottom: 0.75rem;
    border: 1px solid #e5e7eb;
    
    .credential-label {
      font-weight: 600;
      color: #374151;
      font-size: 0.9rem;
    }
    
    .credential-value {
      font-family: 'Monaco', 'Consolas', monospace;
      background: #f3f4f6;
      padding: 0.5rem 0.75rem;
      border-radius: 6px;
      color: #1f2937;
      font-size: 0.9rem;
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .copy-button {
      background: #3b82f6;
      color: white;
      border: none;
      padding: 0.5rem;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 32px;
      min-height: 32px;
      
      &:hover {
        background: #2563eb;
      }
      
      &.copied {
        background: #10b981;
      }
    }
  }
  
  .warning-text {
    color: #dc2626;
    font-size: 0.9rem;
    font-weight: 500;
    text-align: center;
    background: #fef2f2;
    padding: 1rem;
    border-radius: 8px;
    border: 1px solid #fecaca;
    margin-top: 1rem;
  }
`;

// Enhanced Mobile-responsive admin card
const AdminCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  
  .admin-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    gap: 1rem;
    
    .admin-info {
      flex: 1;
      min-width: 0;
      
      .admin-username {
        font-size: 1.1rem;
        font-weight: 600;
        color: #111827;
        margin-bottom: 0.25rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      
      .admin-rank {
        display: inline-block;
        padding: 0.25rem 0.75rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
      }
    }
    
    .actions {
      display: flex;
      gap: 0.5rem;
      flex-shrink: 0;
    }
  }
  
  .admin-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
    
    .detail-item {
      .label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 0.25rem;
      }
      
      .value {
        font-size: 0.9rem;
        color: #111827;
        font-weight: 500;
      }
    }
  }
`;

// Responsive container that switches between table and cards
const ResponsiveContainer = styled.div`
  .desktop-view {
    display: block;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  .mobile-view {
    display: none;
    
    @media (max-width: 768px) {
      display: block;
    }
  }
`;

const AdminsPage = () => {
  const [admins, setAdmins] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [rankFilter, setRankFilter] = useState('all');
  const [adminStats, setAdminStats] = useState(null);
  const [createdAdminCredentials, setCreatedAdminCredentials] = useState(null);
  const [copiedFields, setCopiedFields] = useState({});

  const [newAdmin, setNewAdmin] = useState({
    username: '',
    rank: 'manager'
  });

  const adminRank = localStorage.getItem('adminRank');

  useEffect(() => {
    if (adminRank === 'owner') {
      fetchAdmins();
    }
  }, [adminRank]);

  useEffect(() => {
    const filtered = admins.filter(admin => {
      const matchesSearch = !searchTerm || 
        admin.username.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesRank = rankFilter === 'all' || admin.rank === rankFilter;

      return matchesSearch && matchesRank;
    });

    setFilteredAdmins(filtered);
  }, [admins, searchTerm, rankFilter]);

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      const res = await axios.get('/api/admin/admins', { headers });
      setAdmins(res.data.admins);
      
      // Calculate stats
      const stats = {
        total_admins: res.data.admins.length,
        owners: res.data.admins.filter(admin => admin.rank === 'owner').length,
        managers: res.data.admins.filter(admin => admin.rank === 'manager').length,
        recent_logins: res.data.admins.filter(admin => 
          admin.last_login && new Date() - new Date(admin.last_login) < 7 * 24 * 60 * 60 * 1000
        ).length
      };
      setAdminStats(stats);
      
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch admins');
      setLoading(false);
      toast.error('Failed to fetch admins');
    }
  };

  const generateSecurePassword = () => {
    const length = 16;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+-=';
    let password = '';
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)];
    password += '0123456789'[Math.floor(Math.random() * 10)];
    password += '!@#$%^&*()_+-='[Math.floor(Math.random() * 14)];
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const password = generateSecurePassword();
      
      const res = await axios.post('/api/admin/create-admin', {
        ...newAdmin,
        password
      }, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      if (res.data.success) {
        setCreatedAdminCredentials({
          username: newAdmin.username,
          password,
          rank: newAdmin.rank
        });
        
        setNewAdmin({
          username: '',
          rank: 'manager'
        });
        
        setShowCreateAdmin(false);
        fetchAdmins();
        toast.success('Admin created successfully!');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to create admin';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      const res = await axios.delete(`/api/admin/admins/${adminId}`, { headers });
      
      if (res.data.success) {
        fetchAdmins();
        toast.success('Admin deleted successfully!');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to delete admin';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedFields({ ...copiedFields, [field]: true });
      setTimeout(() => {
        setCopiedFields({ ...copiedFields, [field]: false });
      }, 2000);
      toast.success('Copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const refreshAdmins = () => {
    fetchAdmins();
  };

  const exportAdmins = () => {
    const csvContent = [
      ['Username', 'Rank', 'Created', 'Last Login'],
      ...filteredAdmins.map(admin => [
        admin.username,
        admin.rank,
        formatDate(admin.created_at),
        formatDate(admin.last_login)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'admins_export.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

     const getRankBadge = (rank) => {
     if (rank === 'owner') {
       return { background: '#dcfce7', color: '#166534', text: 'Owner', icon: <FiStar /> };
     }
     return { background: '#dbeafe', color: '#1d4ed8', text: 'Manager', icon: <FiShield /> };
   };

   const getRankIcon = (rank) => {
     return rank === 'owner' ? <FiStar /> : <FiShield />;
   };

  if (adminRank !== 'owner') {
    return (
      <AdminLayout>
        <AdminContainer>
          <AdminHeader>
            <HeaderContent>
              <HeaderText>
                <h1>Access Denied</h1>
                <p>Only owners can view admin accounts</p>
              </HeaderText>
            </HeaderContent>
          </AdminHeader>
        </AdminContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminContainer>
        <AdminHeader>
          <HeaderContent>
            <HeaderText>
              <h1>Admin Management</h1>
              <p>Manage administrative accounts and permissions</p>
            </HeaderText>
            <HeaderActions>
              <ActionButton onClick={() => setShowCreateAdmin(true)}>
                <FiPlus />
                <span className="btn-text">Create Admin</span>
              </ActionButton>
              <ActionButton onClick={refreshAdmins} variant="secondary">
                <FiRefreshCw />
                <span className="btn-text">Refresh</span>
              </ActionButton>
              <ActionButton onClick={exportAdmins} variant="secondary">
                <FiDownload />
                <span className="btn-text">Export CSV</span>
              </ActionButton>
            </HeaderActions>
          </HeaderContent>
        </AdminHeader>

        <MainContent>
          {/* Admin Stats Section */}
          {adminStats && (
            <StatsGrid>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiUsers color="#3b82f6" /></StatIcon>
                  <h3>Total Admins</h3>
                </StatHeader>
                <StatValue>{adminStats.total_admins}</StatValue>
                <StatLabel>System Administrators</StatLabel>
              </StatCard>
                               <StatCard>
                   <StatHeader>
                     <StatIcon><FiStar color="#f59e0b" /></StatIcon>
                     <h3>Owners</h3>
                   </StatHeader>
                   <StatValue>{adminStats.owners}</StatValue>
                   <StatLabel>Full Access Level</StatLabel>
                 </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiShield color="#8b5cf6" /></StatIcon>
                  <h3>Managers</h3>
                </StatHeader>
                <StatValue>{adminStats.managers}</StatValue>
                <StatLabel>Standard Access Level</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiSettings color="#10b981" /></StatIcon>
                  <h3>Recent Logins</h3>
                </StatHeader>
                <StatValue>{adminStats.recent_logins}</StatValue>
                <StatLabel>Last 7 Days</StatLabel>
              </StatCard>
            </StatsGrid>
          )}

          <SearchAndFilters>
            <SearchBox>
              <FiSearch />
              <input
                type="text"
                placeholder="Search admins..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBox>
            <FilterSelect value={rankFilter} onChange={(e) => setRankFilter(e.target.value)}>
              <option value="all">All Ranks</option>
              <option value="owner">Owners</option>
              <option value="manager">Managers</option>
            </FilterSelect>
          </SearchAndFilters>

          <ActionButtonGroup>
            <ResultsCounter>{filteredAdmins.length} admins found</ResultsCounter>
          </ActionButtonGroup>

          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : (
            <ResponsiveContainer>
              {/* Desktop Table View */}
              <div className="desktop-view">
                <DataTable>
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Rank</th>
                      <th>Created</th>
                      <th>Last Login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAdmins.map((admin, index) => {
                      const rankBadge = getRankBadge(admin.rank);
                      
                      return (
                        <tr key={admin.id || index}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {getRankIcon(admin.rank)}
                              <span style={{ fontWeight: '500' }}>{admin.username}</span>
                            </div>
                          </td>
                          <td>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '0.8em',
                              fontWeight: '500',
                              background: rankBadge.background,
                              color: rankBadge.color,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}>
                              {rankBadge.icon}
                              {rankBadge.text}
                            </span>
                          </td>
                          <td>{formatDate(admin.created_at)}</td>
                          <td>{formatDate(admin.last_login)}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <TableActionButton 
                                onClick={() => handleDeleteAdmin(admin.id)}
                                variant="danger"
                                title="Delete Admin"
                              >
                                <FiTrash2 />
                              </TableActionButton>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </DataTable>
              </div>

              {/* Mobile Card View */}
              <div className="mobile-view">
                {filteredAdmins.map((admin, index) => {
                  const rankBadge = getRankBadge(admin.rank);
                  
                  return (
                    <AdminCard key={admin.id || index}>
                      <div className="admin-header">
                        <div className="admin-info">
                          <div className="admin-username">
                            {getRankIcon(admin.rank)}
                            {admin.username}
                          </div>
                          <span className="admin-rank" style={{
                            background: rankBadge.background,
                            color: rankBadge.color
                          }}>
                            {rankBadge.text}
                          </span>
                        </div>
                        <div className="actions">
                          <TableActionButton 
                            onClick={() => handleDeleteAdmin(admin.id)}
                            variant="danger"
                            title="Delete Admin"
                          >
                            <FiTrash2 />
                          </TableActionButton>
                        </div>
                      </div>
                      
                      <div className="admin-details">
                        <div className="detail-item">
                          <div className="label">Created Date</div>
                          <div className="value">{formatDate(admin.created_at)}</div>
                        </div>
                        <div className="detail-item">
                          <div className="label">Last Login</div>
                          <div className="value">{formatDate(admin.last_login)}</div>
                        </div>
                      </div>
                    </AdminCard>
                  );
                })}
              </div>
            </ResponsiveContainer>
          )}

          {/* Create Admin Modal */}
          {showCreateAdmin && (
            <Modal>
              <ModalContent>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h2>Create Admin Account</h2>
                  <button 
                    onClick={() => setShowCreateAdmin(false)}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      fontSize: '1.5rem', 
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0.5rem',
                      borderRadius: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleCreateAdmin}>
                  <ModalFormGroup>
                    <label>Username</label>
                    <input
                      type="text"
                      value={newAdmin.username}
                      onChange={(e) => setNewAdmin({...newAdmin, username: e.target.value})}
                      placeholder="Enter admin username"
                      required
                    />
                  </ModalFormGroup>

                  <ModalFormGroup>
                    <label>Rank</label>
                    <select
                      value={newAdmin.rank}
                      onChange={(e) => setNewAdmin({...newAdmin, rank: e.target.value})}
                      required
                    >
                      <option value="manager">Manager</option>
                      <option value="owner">Owner</option>
                    </select>
                  </ModalFormGroup>

                  <ModalActions>
                    <ActionButton 
                      type="button" 
                      onClick={() => setShowCreateAdmin(false)} 
                      variant="secondary"
                    >
                      Cancel
                    </ActionButton>
                    <ActionButton type="submit">
                      <FiPlus />
                      Create Admin
                    </ActionButton>
                  </ModalActions>
                </form>
              </ModalContent>
            </Modal>
          )}

          {/* Credentials Display Modal */}
          {createdAdminCredentials && (
            <Modal>
              <ModalContent>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h2>🎉 Admin Account Created</h2>
                  <button 
                    onClick={() => setCreatedAdminCredentials(null)}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      fontSize: '1.5rem', 
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0.5rem',
                      borderRadius: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FiX />
                  </button>
                </div>

                <CredentialsDisplay>
                  <div className="credential-item">
                    <span className="credential-label">Username:</span>
                    <code className="credential-value">{createdAdminCredentials.username}</code>
                    <button 
                      className={`copy-button ${copiedFields.username ? 'copied' : ''}`}
                      onClick={() => copyToClipboard(createdAdminCredentials.username, 'username')}
                    >
                      {copiedFields.username ? <FiCheck /> : <FiCopy />}
                    </button>
                  </div>

                  <div className="credential-item">
                    <span className="credential-label">Password:</span>
                    <code className="credential-value">{createdAdminCredentials.password}</code>
                    <button 
                      className={`copy-button ${copiedFields.password ? 'copied' : ''}`}
                      onClick={() => copyToClipboard(createdAdminCredentials.password, 'password')}
                    >
                      {copiedFields.password ? <FiCheck /> : <FiCopy />}
                    </button>
                  </div>

                  <div className="credential-item">
                    <span className="credential-label">Rank:</span>
                    <code className="credential-value">{createdAdminCredentials.rank}</code>
                    <button 
                      className={`copy-button ${copiedFields.rank ? 'copied' : ''}`}
                      onClick={() => copyToClipboard(createdAdminCredentials.rank, 'rank')}
                    >
                      {copiedFields.rank ? <FiCheck /> : <FiCopy />}
                    </button>
                  </div>

                  <div className="warning-text">
                    ⚠️ Please save these credentials securely. The password cannot be recovered later and will not be shown again.
                  </div>
                </CredentialsDisplay>

                <ModalActions>
                  <ActionButton 
                    onClick={() => setCreatedAdminCredentials(null)}
                  >
                    I've Saved The Credentials
                  </ActionButton>
                </ModalActions>
              </ModalContent>
            </Modal>
          )}
        </MainContent>
      </AdminContainer>
    </AdminLayout>
  );
};

export default AdminsPage; 